<template>
  <ClickWrapper
      ref="root"
      class="io-calculator__usage-field"
      :class="{ 'open': isOpen }"
      :style="cssVars"
      @outside="isOpen = false"
  >
    <InputField
        :label="label"
        type="number"
        v-model="value"
        :min="minUsage"
        :max="maxUsage"
        :only-numeric="true"
        :showValidCheck="showValidCheck"
        :is-valid="isValid"
        :valid-color="validColor"
        @focusin="isOpen = true"
        @keyup="onKeyUp"
    />
    <div
        v-show="isOpen"
        class="io-calculator__usage-field__popover"
        ref="popover"
    >
      <div
          class="io-calculator__usage-field__popover__icons"
          ref="iconRowWrapper"
      >
        <div
            class="row g-2"
            ref="iconRow"
        >
          <div class="col"></div>
          <div class="col-auto">
            <component
                :is="preselectIcons[0]"
                :color="iconColor[0]"
                @click="onClickIcon(0)"
            />
          </div>
          <div class="col"></div>
          <div class="col-auto">
            <component
                :is="preselectIcons[1]"
                :color="iconColor[1]"
                @click="onClickIcon(1)"
            />
          </div>
          <div class="col"></div>
          <div class="col-auto">
            <component
                :is="preselectIcons[2]"
                :color="iconColor[2]"
                @click="onClickIcon(2)"
            />
          </div>
          <div class="col"></div>
          <div class="col-auto">
            <component
                :is="preselectIcons[3]"
                :color="iconColor[3]"
                @click="onClickIcon(3)"
            />
          </div>
          <div class="col"></div>
        </div>
      </div>
      <div class="io-calculator__usage-field__popover__slider">
        <IoSlider
            @change="updateSliderValue"
            v-model="sliderDragValue"
            :max="maxUsage"
            :step="1"
            :tooltip="true"
            tooltip-show="drag"
            :tooltip-format="formatTooltip"
        />
      </div>
    </div>
  </ClickWrapper>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  onBeforeMount,
  watch,
  nextTick,
} from 'vue';

import InputField from '@/components/InputField.vue';
import IoSlider from '@/components/inputs/Slider.vue';
import ClickWrapper from '@/components/ClickWrapper.vue';
import IconPower1 from '@/components/icons/IconPower1.vue';
import IconPower2 from '@/components/icons/IconPower2.vue';
import IconPower3 from '@/components/icons/IconPower3.vue';
import IconPower4 from '@/components/icons/IconPower4.vue';
import IconGas1 from '@/components/icons/IconGas1.vue';
import IconGas2 from '@/components/icons/IconGas2.vue';
import IconGas3 from '@/components/icons/IconGas3.vue';
import IconGas4 from '@/components/icons/IconGas4.vue';
import { env, formatNumber } from '@/helpers';

const modelValue = defineModel();
const props = withDefaults(defineProps<{
  label: string
  noInitialValue?: boolean
  showValidCheck?: boolean
  branch?: string
  isValid?: boolean
  validColor?: string | Array<string | undefined>
}>(), {
  branch: 'power',
});

const emit = defineEmits([
  'keyup',
  'input',
]);

const root = ref<InstanceType<typeof ClickWrapper>>();
const popover = ref<InstanceType<typeof HTMLElement>>();
const iconRow = ref<InstanceType<typeof HTMLElement>>();
const iconRowWrapper = ref<InstanceType<typeof HTMLElement>>();

const percentage = ref(25);
const isOpen = ref(false);
const selectedIcon = ref(1);

const cssVars = computed(() => {
  let iconColorActive = Array.isArray(props.validColor) ? props.validColor[0] : 'var(--color-brand-primary)';
  if (!iconColorActive) {
    iconColorActive = 'var(--color-brand-primary)';
  }

  return {
    '--icon-color-active': iconColorActive,
  };
});

const iconColor = computed(() => [
  selectedIcon.value === 1 ? 'var(--icon-color-active)' : '#DBDBDB',
  selectedIcon.value === 2 ? 'var(--icon-color-active)' : '#DBDBDB',
  selectedIcon.value === 3 ? 'var(--icon-color-active)' : '#DBDBDB',
  selectedIcon.value === 4 ? 'var(--icon-color-active)' : '#DBDBDB',
]);

const onKeyUp = (e: unknown) => {
  emit('keyup', e);
};

const preselectIcons = computed(() => [
  props.branch === 'gas' ? IconGas1 : IconPower1,
  props.branch === 'gas' ? IconGas2 : IconPower2,
  props.branch === 'gas' ? IconGas3 : IconPower3,
  props.branch === 'gas' ? IconGas4 : IconPower4,
]);

const preSelectValues = computed(() => {
  switch (props.branch) {
    case 'gas':
      return [
        5000,
        12000,
        18000,
        20000,
      ];
    default:
      return [
        1200,
        2000,
        3000,
        4250,
      ];
  }
});

const maxUsage = computed(() => {
  switch (props.branch) {
    case 'gas':
      return env('CALCULATOR_MAX_USAGE_GAS', 50000) as number;
    default:
      return env('CALCULATOR_MAX_USAGE_POWER', 10000) as number;
  }
});

const minUsage = computed(() => {
  switch (props.branch) {
    case 'gas':
      return env('CALCULATOR_MIN_USAGE_GAS', 600) as number;
    default:
      return env('CALCULATOR_MIN_USAGE_POWER', 100) as number;
  }
});

const sliderSteps = computed(() => {
  const steps = [];

  const pv = preSelectValues.value;
  const maxu = maxUsage.value;
  const minu = minUsage.value;

  const stepSize = maxu / (pv.length + 1);
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < (pv.length + 1); i += 1) {
    const stepMin = pv[i - 1] || minu;
    const stepMax = pv[i] ? (pv[i] - 1) : maxu;

    steps.push({
      size: stepSize,
      range: stepMax - stepMin,
      min: stepMin,
      max: stepMax,
      percentage: ((stepSize / maxu) * i * 100),
    });
  }

  return steps;
});

const getCurrentStepByPercentage = (pc: number) => {
  const pv = preSelectValues.value;
  const mu = maxUsage.value;

  const stepSize = mu / (pv.length + 1);
  // eslint-disable-next-line no-bitwise
  let currentStep = ~~(pc / stepSize);
  if (currentStep > (sliderSteps.value.length - 1)) {
    currentStep = sliderSteps.value.length - 1;
  }
  const step = sliderSteps.value[currentStep];
  const stepPercentage = (pc - (currentStep * step.size)) / step.size;

  return {
    num: currentStep,
    size: step.size,
    min: step.min,
    max: step.max,
    percentage: stepPercentage,
  };
};

const getPercentageByValue = (v: number) => {
  const cstep = sliderSteps.value.filter((s) => s.min <= v && s.max >= v)[0];

  if (cstep) {
    const cstepPercentage = (v - cstep.min) / cstep.range;
    const cp = (cstepPercentage * ((cstep.size / maxUsage.value) * 100)) + cstep.percentage;

    return (cp / 100) * maxUsage.value;
  }

  return 0;
};

const calculateValueByPercentage = (percent: number) => {
  const step = getCurrentStepByPercentage(percent);

  selectedIcon.value = step.num;

  return Math.ceil(step.min + ((step.max - step.min) * step.percentage));
};

const sliderDragValue = ref(preSelectValues.value[0]);

const value = computed({
  get(): unknown {
    return modelValue.value;
  },
  set(newValue: unknown): void {
    sliderDragValue.value = getPercentageByValue(newValue as number);
    modelValue.value = newValue;
  },
});

const onClickIcon = (iconInx: number) => {
  value.value = preSelectValues.value[iconInx];

  if (/Android|Mobi|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    isOpen.value = false;
  }
};

onBeforeMount(() => {
  if (value.value) {
    // eslint-disable-next-line prefer-destructuring
    const val = value.value as number;
    if (!value.value || val < minUsage.value || val > maxUsage.value) {
      // eslint-disable-next-line prefer-destructuring
      value.value = preSelectValues.value[0];
    }
  } else if (!props.noInitialValue) {
    // eslint-disable-next-line prefer-destructuring
    value.value = preSelectValues.value[0];
  }

  percentage.value = getPercentageByValue((value.value || 0) as number);
  sliderDragValue.value = percentage.value;
});

const updateSliderValue = (val: number) => {
  value.value = calculateValueByPercentage(val);
};

const formatTooltip = (v: number) => `${formatNumber(calculateValueByPercentage(v), 0)} kWh`;

watch(() => isOpen.value, () => {
  if (isOpen.value) {
    nextTick(() => {
      if (root.value && popover.value) {
        popover.value.style.minWidth = '';
        popover.value.style.position = 'fixed';
        popover.value.style.position = 'absolute';
        popover.value.style.minWidth = 'calc(100% + 2em)';
      }
    });
  }
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.io-calculator__usage-field {
  position: relative;

  [type="number"] {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    -moz-appearance: textfield;
  }

  &.open {
    .input-field {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .valid-check {
      display: none !important;
    }
  }

  &__popover {
    position: absolute;
    z-index: 10;
    left: -1em;
    border-radius: 7px 7px 0 0;
    border: 0;
    box-shadow: 0 -6px 6px rgba(0, 0, 0, .16);
    bottom: calc(100% + 1em);
    height: auto;
    @include font-regular;
    padding: 1em;
    color: #38383A;
    background-color: #FFFFFF;
    border-bottom: 1px solid #DBDBDB;
    width: 15em !important;

    &__icons {
      @include font-regular(26px);
      margin-bottom: 1em;

      .row {
        flex-wrap: nowrap;
      }

      svg {
        display: inline-block;
        max-width: 100%;

        @include responsive-size-sm(54);
        height: 1em;
        width: auto;
        cursor: pointer;

        &:hover, &.active {
          [stroke="#DBDBDB"] {
            stroke: var(--icon-color-active);
          }
        }
      }
    }
  }
}
</style>
